import { useId } from "react";
import clsx from "clsx";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";
import { VIEW_COLORS } from "@/shared/utils/savedViewHighlight";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";
import { useEnsureWatchlistAlerts } from "@/shared/hooks/useEnsureWatchlistAlerts";
import {
  isNotificationMethodEnabled,
  type FilterNotifications,
} from "@/types/notifications";

const COLOR_NAMES = ["Newsquawk blue", "Amber", "Green", "Magenta", "Teal", "Yellow"];

interface WhenMatchesSectionProps {
  color: string;
  onColorChange: (next: string) => void;
  notifications: FilterNotifications;
  onNotificationsChange: (next: FilterNotifications) => void;
  /** A Newsquawk list's colour kept for this user, which can be taken back. */
  onResetColor?: () => void;
}

/**
 * What happens when a headline matches: the colour it's highlighted in, and
 * the alerts it sends. One set of alerts, saying what each covers once one
 * is ticked.
 */
export default function WhenMatchesSection({
  color,
  onColorChange,
  notifications,
  onNotificationsChange,
  onResetColor,
}: WhenMatchesSectionProps) {
  const customId = useId();
  const { watchlistAlertMode } = useWatchlistAlertMode();
  const { ensurePopup } = useEnsureWatchlistAlerts();
  const upper = color.toUpperCase();
  const custom = !VIEW_COLORS.includes(upper);
  const email = isNotificationMethodEnabled(notifications, "email");
  const inPage =
    isNotificationMethodEnabled(notifications, "sound") ||
    isNotificationMethodEnabled(notifications, "popup");

  return (
    <section
      aria-label="When a headline matches"
      className="flex flex-col gap-3 border-t border-gray-800 pt-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-[92px] shrink-0 text-[13px] text-gray-300">Highlight in</span>
        <div className="flex flex-wrap items-center gap-2">
          {VIEW_COLORS.map((swatch, i) => {
            const on = upper === swatch;
            return (
              <button
                key={swatch}
                type="button"
                onClick={() => onColorChange(swatch)}
                aria-label={`Highlight in ${COLOR_NAMES[i]}`}
                aria-pressed={on}
                className={clsx(
                  "size-6 rounded-full border-2 p-0 shadow-[inset_0_0_0_2px_#111827] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 motion-reduce:transition-none",
                  on ? "border-gray-100" : "border-transparent hover:scale-110"
                )}
                style={{ backgroundColor: swatch }}
              />
            );
          })}
          <label
            htmlFor={customId}
            className="ml-1.5 flex cursor-pointer items-center gap-1.5 text-[13px] text-gray-300"
          >
            <input
              id={customId}
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : VIEW_COLORS[0]}
              onChange={(e) => onColorChange(e.target.value.toUpperCase())}
              className={clsx(
                "h-6 w-7 cursor-pointer rounded border bg-transparent p-0",
                custom ? "border-gray-100" : "border-gray-600"
              )}
            />
            Custom
          </label>
          {onResetColor && (
            <button
              type="button"
              onClick={onResetColor}
              className="ml-2 rounded border-0 bg-transparent p-0 text-xs font-normal text-gray-400 transition-colors hover:text-squawk-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
            >
              Reset to Newsquawk&apos;s colour
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <span className="w-[92px] shrink-0 pt-px text-[13px] text-gray-300">Alert me by</span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <NotificationMethodCheckboxes
            value={notifications}
            onChange={onNotificationsChange}
            // Pop-up asks the browser for permission, which it only allows
            // from the click itself.
            onEnableMethod={(method) => {
              if (method === "popup") ensurePopup();
            }}
            variant="inline"
          />
          {(email || inPage) && (
            <p className="m-0 text-xs text-gray-400">
              Email covers Newsquawk headlines. Sound and pop-ups cover your news feeds while
              Newsquawk is open.
            </p>
          )}
          {inPage && watchlistAlertMode === "off" && (
            <p className="m-0 text-xs text-gray-300">
              Watchlist Alert Mode is off, so sound and pop-ups won&apos;t play yet. Set it in{" "}
              <a
                href="/settings/notifications"
                target="_blank"
                rel="noreferrer"
                className="text-squawk-blue underline-offset-2 hover:text-squawk-blue hover:underline"
              >
                Settings → Notifications
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
